import { ReactComponent as CheckboxIndeterminate } from "assets/svg/checkbox-indeterminate.svg";
import { ReactComponent as CheckboxOff } from "assets/svg/checkbox-off.svg";
import { ReactComponent as CheckboxOn } from "assets/svg/checkbox-on.svg";
import { ReactComponent as RadioOff } from "assets/svg/radio-off.svg";
import { ReactComponent as RadioOn } from "assets/svg/radio-on.svg";
import IconButton from "components/icon-button";
import React, {
  ComponentPropsWithoutRef,
  useCallback,
  useEffect,
  useRef,
} from "react";
import getClasses from "utils/getClasses";
import styles from "./CustomSelectionControl.module.scss";
import { useBlockType } from "components/Dashboard/DoctorDashboard/notes-and-forms/components/template-editor/components/block-type/contexts/BlockTypeContext";

type Props = ComponentPropsWithoutRef<typeof IconButton> & {
  type: "checkbox" | "radio";
  checked?: boolean;
  onChange?: (
    arg: React.MouseEvent & {
      target: React.MouseEvent["target"] & { checked: boolean };
    },
  ) => void;
  indeterminate?: boolean;
  className?: string;
};

const CustomSelectionControl = ({
  type,
  checked,
  onChange,
  className,
  indeterminate,
  onClick,
  ...props
}: Props) => {
  const { isDownload } = useBlockType();
  const shouldUseNativeInputs = isDownload;

  const clickHandler = useCallback(
    (e) => {
      // similar to native event
      e.target.checked = !checked;
      if (onChange) {
        onChange(e);
      }
      if (onClick) {
        onClick(e);
      }
    },
    [checked, onChange, onClick],
  );

  const checkboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = !!indeterminate;
    }
  }, [indeterminate]);

  if (!shouldUseNativeInputs) {
    return (
      <IconButton
        className={getClasses(styles["custom-selection-control"], className)}
        onClick={clickHandler}
        {...props}
      >
        {type === "radio" ? (
          checked ? (
            <RadioOn />
          ) : (
            <RadioOff />
          )
        ) : indeterminate ? (
          <CheckboxIndeterminate />
        ) : checked ? (
          <CheckboxOn />
        ) : (
          <CheckboxOff />
        )}
      </IconButton>
    );
  }

  if (type === "radio") {
    return (
      <input
        type="radio"
        className={styles["native-input"]}
        readOnly
        checked={checked}
      />
    );
  }

  return (
    <input
      type="checkbox"
      ref={checkboxRef}
      className={styles["native-input"]}
      readOnly
      checked={checked}
    />
  );
};

export default CustomSelectionControl;
